// src/components/MyProductsPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Edit, Trash2, Plus, Search, Package, 
    Loader2, AlertCircle, CheckCircle, X, 
} from 'lucide-react';

// --- TOAST COMPONENT ---
const Toast = ({ show, message, type, onClose }: any) => {
    if (!show) return null;
    const isSuccess = type === 'success';
    return (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[110] flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl border animate-in slide-in-from-top-5 fade-in duration-300 ${isSuccess ? 'bg-white border-green-200 text-green-800' : 'bg-white border-red-200 text-red-800'}`}>
            <div className={`p-1.5 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                {isSuccess ? <CheckCircle size={18} className="text-green-600"/> : <AlertCircle size={18} className="text-red-600"/>}
            </div>
            <div>
                <h4 className="text-sm font-bold">{isSuccess ? 'Success' : 'Error'}</h4>
                <p className="text-xs font-medium opacity-90">{message}</p>
            </div>
            <button onClick={onClose} className="ml-4 p-1 hover:bg-black/5 rounded-full"><X size={14}/></button>
        </div>
    );
};

const MyProductsPage: React.FC = () => {
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // UI States
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [deleteId, setDeleteId] = useState<number | null>(null); // For delete modal

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // --- FETCH PRODUCTS ---
    const fetchMyProducts = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const response = await fetch('/springboot/api/seller/products/my-products', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setProducts(await response.json());
            } else {
                showToast("Failed to load products.", "error");
            }
        } catch (err) { 
            console.error(err);
            showToast("Server connection error.", "error"); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchMyProducts(); }, []);

    // --- DELETE LOGIC ---
    const confirmDelete = async () => {
        if (!deleteId) return;
        
        const token = localStorage.getItem('sellerToken');
        try {
            const response = await fetch(`/springboot/api/seller/products/${deleteId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setProducts(products.filter(p => p.id !== deleteId));
                showToast("Product deleted successfully.");
            } else {
                showToast("Failed to delete product.", "error");
            }
        } catch (error) {
            showToast("Error deleting product.", "error");
        } finally {
            setDeleteId(null); // Close modal
        }
    };

    // --- FILTERING ---
    const filteredProducts = products.filter(product => 
        product.productName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-10 text-center text-[#00635e] font-bold animate-pulse">Loading Inventory...</div>;

    return (
        <div className="bg-gray-50/50 min-h-screen font-sans relative">
            
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            {/* --- DELETE MODAL --- */}
            {deleteId && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
                        <div className="p-6 text-center">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
                                <Trash2 size={24} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Delete Product?</h3>
                            <p className="text-sm text-gray-500 mt-2">Are you sure you want to remove this item? This action cannot be undone.</p>
                        </div>
                        <div className="flex border-t border-gray-100 bg-gray-50">
                            <button onClick={() => setDeleteId(null)} className="flex-1 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 transition">Cancel</button>
                            <button onClick={confirmDelete} className="flex-1 py-3 text-sm font-bold text-red-600 hover:bg-red-50 transition border-l border-gray-100">Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- HEADER --- */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-xl md:text-2xl font-black text-[#00635e] flex items-center gap-2">
                                <Package className="text-red-600" /> My Products
                            </h1>
                            <p className="text-xs text-gray-500 mt-1">Manage your inventory, pricing, and visibility.</p>
                        </div>

                        <div className="flex gap-3 w-full md:w-auto">
                            {/* Search Bar */}
                            <div className="relative flex-1 md:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input 
                                    type="text" 
                                    placeholder="Search products..." 
                                    value={searchTerm} 
                                    onChange={(e) => setSearchTerm(e.target.value)} 
                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-[#00635e] focus:border-[#00635e] outline-none transition-all bg-gray-50 focus:bg-white"
                                />
                            </div>
                            
                            {/* Add Button */}
                            <Link 
                                to="/seller/add-product" 
                                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700 shadow-md transition-all active:scale-95 whitespace-nowrap"
                            >
                                <Plus size={18} /> <span className="hidden sm:inline">Add New</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- CONTENT GRID --- */}
            <div className="max-w-7xl mx-auto p-4 md:p-6 pb-20">
                {filteredProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl bg-white/50">
                        <Package size={48} className="mb-3 opacity-20" />
                        <p className="text-sm font-medium">No products found matching your search.</p>
                        <Link to="/seller/add-product" className="mt-4 text-[#00635e] font-bold text-sm hover:underline">Add your first product</Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-in fade-in duration-300">
                        {filteredProducts.map((product) => {
                            const mainImage = product.images && product.images.length > 0 ? product.images[0].imageUrl : null;
                            const isApproved = product.status === 'APPROVED';

                            return (
                                <div key={product.id} className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden relative">
                                    
                                    {/* Image Area */}
                                    <div className="h-48 bg-gray-100 relative overflow-hidden">
                                        {mainImage ? (
                                            <img src={mainImage} alt={product.productName} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                                                <Package size={40} />
                                            </div>
                                        )}
                                        
                                        {/* Status Badge */}
                                        <div className="absolute top-3 left-3">
                                            <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-md shadow-sm backdrop-blur-md ${isApproved ? 'bg-green-500/90 text-white' : 'bg-yellow-500/90 text-white'}`}>
                                                {product.status || 'PENDING'}
                                            </span>
                                        </div>

                                        {/* Hover Actions */}
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 backdrop-blur-[2px]">
                                            <Link 
                                                to={`/seller/edit-product/${product.id}`} 
                                                className="p-2 bg-white rounded-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 shadow-lg transform hover:scale-110 transition-all"
                                                title="Edit"
                                            >
                                                <Edit size={18} />
                                            </Link>
                                            <button 
                                                onClick={() => setDeleteId(product.id)} 
                                                className="p-2 bg-white rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 shadow-lg transform hover:scale-110 transition-all"
                                                title="Delete"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Content Area */}
                                    <div className="p-4 flex flex-col flex-1">
                                        <div className="flex justify-between items-start mb-1">
                                            <h3 className="font-bold text-gray-800 text-sm line-clamp-2 leading-snug" title={product.productName}>
                                                {product.productName}
                                            </h3>
                                        </div>
                                        
                                        <div className="mt-auto pt-3 flex items-end justify-between border-t border-gray-50">
                                            <div>
                                                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Price</p>
                                                <p className="text-lg font-black text-[#00635e]">₹ {Number(product.price).toLocaleString()}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Stock</p>
                                                <p className="text-xs font-bold text-gray-700">{product.stock || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyProductsPage;